import { useState, useRef, useEffect } from 'react';
import useDeleteCompanyFile from '../api/useDeleteCompanyFile';
import usePostCompanyFile from '../api/usePostCompanyFile';
import { AlertDialog, AlertDialogContent } from '@/components/ui/alert-dialog';
import { Button } from '@/components/Button';
import { showErrorToast } from '../../job_change_processes/lib/toast';
import clsx from 'clsx';
import type { RefetchOptions, QueryObserverResult } from '@tanstack/react-query';
import type { AgentApiCompaniesShow } from '@/types/serializers';
import { MdFolder } from 'react-icons/md';

type Props = {
  companyId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  files: {
    id: number;
    filename: string;
    url: string;
  }[];
  companyRefetch: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<AgentApiCompaniesShow, Error>>;
};

export default function CompaniesFileUploadModal({
  open,
  onOpenChange,
  files,
  companyId,
  companyRefetch,
}: Props) {
  const { mutateAsync: deleteFile } = useDeleteCompanyFile();
  const { mutateAsync: postFile } = usePostCompanyFile();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<Props['files'][0] | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropAreaRef = useRef<HTMLDivElement>(null);

  const uploadFile = async (file: File) => {
    try {
      await postFile({ companyId, file });
      await companyRefetch();
    } catch (error) {
      showErrorToast('ファイルのアップロードに失敗しました');
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const file = input.files?.[0];

    if (!file) {
      showErrorToast('ファイルが選択されていません');
      return;
    }

    await uploadFile(file);
    input.value = '';
  };

  const handleOpenFile = (url: string) => {
    if (!url) {
      showErrorToast('ファイルURLが無効です');
      return;
    }

    try {
      const absoluteUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;
      window.open(absoluteUrl, '_blank');
    } catch (error) {
      showErrorToast('ファイルを開けませんでした');
    }
  };

  const handleDownloadFile = async (url: string, filename: string) => {
    if (!url || !filename) {
      showErrorToast('ダウンロード情報が無効です');
      return;
    }

    try {
      const absoluteUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;

      const response = await fetch(absoluteUrl);
      if (!response.ok) throw new Error('ファイル取得に失敗');

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      }, 100);
    } catch (error) {
      showErrorToast('ダウンロードに失敗しました');
    }
  };

  const handleDeleteFile = async (fileId: number) => {
    try {
      await deleteFile({ companyId, fileId });
      setIsMenuOpen(false);
      await companyRefetch();
    } catch (error) {
      showErrorToast('ファイルの削除に失敗しました');
    }
  };

  useEffect(() => {
    if (!isMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const clickedInsideMenu = menuRef.current?.contains(event.target as Node);
      if (!clickedInsideMenu) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isMenuOpen]);

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFiles = e.dataTransfer?.files;
    if (droppedFiles && droppedFiles.length > 0) {
      uploadFile(droppedFiles[0]);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-h-[90vh] max-w-screen-sm overflow-auto">
        <div className="flex h-[56px] items-center gap-1 border-b border-solid border-border px-6">
          <p className="text-base font-bold">ファイル管理</p>
        </div>
        <div className="flex flex-col gap-3 border-b border-solid border-border p-6">
          {files.map((f, key) => {
            return (
              <div
                key={key}
                className="flex h-[59px] items-center rounded-sm border border-solid border-shadow bg-white px-6"
              >
                <img src="/agent/candidates/icon-file.svg" alt="" width={20} height={20} />
                <p className="ml-2 line-clamp-1 text-base">{f.filename}</p>
                <div
                  className="relative ml-auto"
                  ref={menuRef}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    className={clsx(
                      'flex size-[42px] items-center justify-center',
                      isMenuOpen &&
                        selectedFile?.id === f.id &&
                        'rounded border border-solid border-border bg-tag-gray'
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(f);
                      setIsMenuOpen(!isMenuOpen || selectedFile?.id !== f.id);
                    }}
                  >
                    <img src="/agent/candidates/icon-more.svg" alt="" width={24} height={24} />
                  </button>
                  {isMenuOpen && selectedFile?.id === f.id && (
                    <div
                      className="absolute top-full right-0 z-10 w-[130px] rounded bg-white shadow-[0px_0px_12px_0px_#00000033]"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        className="flex h-xxl w-full items-center px-3 hover:opacity-60"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenFile(f.url);
                        }}
                      >
                        <span className="text-sm">開く</span>
                      </button>
                      <button
                        type="button"
                        className="flex h-xxl w-full items-center px-3 hover:opacity-60"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadFile(f.url, f.filename);
                        }}
                      >
                        <span className="text-sm">ダウンロード</span>
                      </button>
                      <button
                        type="button"
                        className="flex h-xxl w-full items-center px-3 hover:opacity-60"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteFile(f.id);
                        }}
                      >
                        <span className="text-sm text-danger">削除する</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          <div
            ref={dropAreaRef}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            className={clsx(
              'flex flex-col items-center justify-center gap-3 rounded border-2 border-dashed',
              isDragging ? 'border-primary bg-primary/10' : 'border-border bg-gray',
              files.length === 0 ? 'h-[224px]' : 'h-[150px]'
            )}
          >
            <img src="/agent/candidates/icon-upload.svg" alt="" width={48} height={48} />
            {files.length === 0 && (
              <div className="flex flex-col items-center justify-center gap-1">
                <p className="text-base">ここにドラッグアンドドロップ</p>
                <p className="text-base">または</p>
              </div>
            )}
            <div>
              <input
                type="file"
                ref={fileInputRef}
                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                className="hidden"
                onChange={handleFileUpload}
              />
              <Button
                type="button"
                color="secondary"
                size="md"
                onClick={() => fileInputRef.current?.click()}
              >
                <MdFolder size={20} />
                ファイルを選択
              </Button>
            </div>
          </div>
        </div>
        <div className="flex h-[74px] items-center justify-end gap-4 px-6">
          <Button type="button" color="secondary" size="md" onClick={() => onOpenChange(false)}>
            キャンセル
          </Button>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}
